import { formatDateTimeISOUtc, getNow } from '@zorro/shared/formatters';
import { useAnalytics } from '@zorro/web/shared-ui';
import {
  downloadFile,
  findTemplateById,
  formatTemplateName,
  showErrorNotification,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

type UseCarrierDocumentDownload = (props: {
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  ichraTemplateEid?: string;
  ichraTemplateTitle?: string;
  /** Names the letter for its enrollment year. Undefined while the period loads. */
  effectiveFrom?: string;
}) => {
  isDownloadingIchra: boolean;
  onDownloadPdf: (selectedTemplate: string | null) => Promise<void>;
  onDownloadIchraLetter: () => Promise<void>;
  onContinueHealthSherpa: () => Promise<void>;
};

export const useCarrierDocumentDownload: UseCarrierDocumentDownload = ({
  employeeId,
  onboardingPeriodId,
  benefitId,
  ichraTemplateEid,
  ichraTemplateTitle,
  effectiveFrom,
}) => {
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { data: templates } = useMonolithQuery({
    method: 'formsFillerControllerGetTemplates',
    params: [],
  });

  const { startLoading, stopLoading } = useLoadingOverlay();
  const { sendEvent } = useAnalytics();
  const [isDownloadingIchra, setIsDownloadingIchra] = useState(false);

  const { mutate: generatePdf } = useMonolithMutation({
    shouldShowLoadingOverlay: false,
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
    method: 'formsFillerControllerGeneratePdf',
  });

  const { mutate: generateDeeplink } = useMonolithMutation({
    method: 'applicationsControllerGenerateDeeplink',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });

  const { mutate: generateIchraLetter } = useMonolithMutation({
    shouldShowLoadingOverlay: false,
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
    method: 'benefitDocumentsControllerGenerateIchraLetter',
  });

  const onDownloadPdf = async (selectedTemplate: string | null) => {
    if (!selectedTemplate || !benefitId) return;

    try {
      startLoading();
      const url = await generatePdf([employeeId, benefitId, selectedTemplate]);
      const templateObj = findTemplateById(templates, selectedTemplate);
      const templateName = formatTemplateName(templateObj?.title || '');
      const employeeFullName = `${employee?.firstName} ${employee?.lastName}`;
      const fileName = `${employeeFullName.replace(/\s+/gu, '-')}_${templateName?.replace(
        /\s+/gu,
        '-'
      )}_${formatDateTimeISOUtc(getNow())}.pdf`;
      downloadFile(url, fileName);
    } finally {
      stopLoading();
    }

    await sendEvent('click_download_pdf', {
      onboarding_period_id: onboardingPeriodId,
      pdf_template_name: findTemplateById(templates, selectedTemplate)?.title,
    });
  };

  const onDownloadIchraLetter = async () => {
    if (!ichraTemplateEid || !effectiveFrom || !employee) return;

    try {
      setIsDownloadingIchra(true);
      const { url, fileName } = await generateIchraLetter([
        onboardingPeriodId,
        employeeId,
        ichraTemplateEid,
      ]);

      downloadFile(url, fileName);

      await sendEvent('click_download_ichra_letter', {
        onboarding_period_id: onboardingPeriodId,
        template_name: ichraTemplateTitle,
      });
    } catch {
      showErrorNotification({
        message: 'Failed to generate ICHRA/CHOICE letter. Please try again.',
      });
    } finally {
      setIsDownloadingIchra(false);
    }
  };

  const onContinueHealthSherpa = async () => {
    if (!benefitId) return;

    const url = await generateDeeplink([employeeId, benefitId]);

    await sendEvent('click_continue_healthsherpa', {
      onboarding_period_id: onboardingPeriodId,
    });
    window.open(url, '_blank');
  };

  return {
    isDownloadingIchra,
    onDownloadPdf,
    onDownloadIchraLetter,
    onContinueHealthSherpa,
  };
};
